'use client';

// Extend Window interface to include posthog
declare global {
  interface Window {
    posthog?: any;
  }
}

import { usePathname } from 'next/navigation';
import { Suspense, useState, useEffect, createContext, useContext } from 'react';
import { cookieManager, CookiePreferences } from '../lib/cookieManager';
import { isCredentialBearingPath, sanitizeAnalyticsPathname } from '../lib/analyticsPrivacy';

// PostHog project API key and host URL from environment variables
const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.posthog.com';

// Context to track PostHog initialization state
interface PostHogContextType {
  isInitialized: boolean;
  initializePostHog: () => void;
}

const PostHogContext = createContext<PostHogContextType>({
  isInitialized: false,
  initializePostHog: () => {},
});

// Initialize PostHog only when user consents
let isPostHogInitialized = false;
let postHogInitialization: Promise<boolean> | null = null;

async function initializePostHogTracking(): Promise<boolean> {
  if (
    typeof window === 'undefined' ||
    !POSTHOG_KEY ||
    isPostHogInitialized ||
    isCredentialBearingPath(window.location.pathname)
  ) {
    return isPostHogInitialized;
  }

  if (postHogInitialization) return postHogInitialization;

  postHogInitialization = (async () => {
    const { default: posthog } = await import('posthog-js');

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,
      autocapture: true,
      disable_session_recording: true,
      persistence: 'memory',
      opt_out_capturing_by_default: true,
      before_send: (event) => {
        if (event && isCredentialBearingPath(window.location.pathname)) {
          return null;
        }
        return event;
      },
    });

    window.posthog = posthog;
    posthog.register({
      platform: 'website',
      source: 'tipkeepr_marketing_site',
      app_version: 'web_1.0'
    });

    if (cookieManager.isAllowed('analytics')) {
      posthog.opt_in_capturing();
      posthog.set_config({ persistence: 'localStorage+cookie' });
    }

    isPostHogInitialized = true;
    return true;
  })();

  return postHogInitialization;
}

// Keep pageview tracking inside the provider's Suspense boundary.
function PageViewTracker(): React.ReactNode {
  const pathname = usePathname();
  const { isInitialized } = useContext(PostHogContext);

  useEffect(() => {
    // Only track pageviews if PostHog is initialized and user consented
    if (
      pathname &&
      !isCredentialBearingPath(pathname) &&
      isInitialized &&
      cookieManager.isAllowed('analytics')
    ) {
      const safePathname = sanitizeAnalyticsPathname(pathname);
      window.posthog?.capture('$pageview', {
        current_url: window.origin + safePathname,
        platform: 'website',
        source: 'tipkeepr_marketing_site',
        page_type: pathname.startsWith('/invite/') ? 'invite' : 'marketing'
      });
    }
  }, [pathname, isInitialized]);

  return null;
}

// Main component with suspense boundary
export function PostHogPageView(): React.ReactNode {
  return (
    <Suspense fallback={null}>
      <PageViewTracker />
    </Suspense>
  );
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isInitialized, setIsInitialized] = useState(false);

  const initializePostHog = async () => {
    if (!isInitialized) {
      const initialized = await initializePostHogTracking();
      setIsInitialized(initialized);
    }
  };

  useEffect(() => {
    // Check if user has already given consent for analytics
    const preferences = cookieManager.getPreferences();
    if (
      preferences &&
      preferences.analytics &&
      pathname &&
      !isCredentialBearingPath(pathname)
    ) {
      initializePostHog();
    }

    // Listen for preference changes
    const unsubscribe = cookieManager.onPreferencesChange((newPreferences: CookiePreferences) => {
      if (newPreferences.analytics && !isInitialized) {
        initializePostHog();
      }
    });

    return unsubscribe;
  }, [isInitialized, pathname]);

  const contextValue: PostHogContextType = {
    isInitialized,
    initializePostHog,
  };

  return (
    <PostHogContext.Provider value={contextValue}>
      {children}
    </PostHogContext.Provider>
  );
}

// Hook to use PostHog context
export function usePostHogContext() {
  return useContext(PostHogContext);
}
